import React, { createContext, useContext, ReactNode } from "react";
import { useAuth as useAuthContext } from "./AuthContext";

export type Role = 'OWNER' | 'MANAGER' | 'EXECUTIVE';

interface Permission {
  canManageUsers: boolean;
  canCreateTemplate: boolean;
  canEditProducts: boolean;
  canViewProducts: boolean;
  canViewPlacementTypes: boolean;
  canViewAdvertisedAsins: boolean;
  canViewKeywordMatchTypes: boolean;
  canViewProductReport: boolean;
}

interface AuthContextType {
  userRole: Role;
  permissions: Permission;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const getPermissionsForRole = (role: Role): Permission => {
  switch (role) {
    case 'OWNER':
      return {
        canManageUsers: true,
        canCreateTemplate: true,
        canEditProducts: true,
        canViewProducts: true,
        canViewPlacementTypes: true,
        canViewAdvertisedAsins: true,
        canViewKeywordMatchTypes: true,
        canViewProductReport: true,
      };
    case 'MANAGER':
      return {
        canManageUsers: true,
        canCreateTemplate: false,
        canEditProducts: true,
        canViewProducts: true,
        canViewPlacementTypes: true,
        canViewAdvertisedAsins: true,
        canViewKeywordMatchTypes: true,
        canViewProductReport: true,
      };
    case 'EXECUTIVE':
      return {
        canManageUsers: false,
        canCreateTemplate: false,
        canEditProducts: false,
        canViewProducts: true,
        canViewPlacementTypes: false,
        canViewAdvertisedAsins: false,
        canViewKeywordMatchTypes: false,
        canViewProductReport: false,
      };
    default:
      return {
        canManageUsers: false,
        canCreateTemplate: false,
        canEditProducts: false,
        canViewProducts: false,
        canViewPlacementTypes: false,
        canViewAdvertisedAsins: false,
        canViewKeywordMatchTypes: false,
        canViewProductReport: false,
      };
  }
};

export const AuthProvider: React.FC<{ role: Role; children: ReactNode }> = ({
  role,
  children,
}) => {
  const permissions = getPermissionsForRole(role);
  return (
    <AuthContext.Provider value={{ userRole: role, permissions }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const useRBAC = () => {
  const { userRole, permissions } = useAuthContext();
  
  const canAssignRole = (targetRole: Role): boolean => {
    switch (userRole) {
      case 'OWNER':
        return true; // Owner can assign any role
      case 'MANAGER':
        return targetRole === 'MANAGER' || targetRole === 'EXECUTIVE'; // Manager can only assign Manager and Executive roles
      case 'EXECUTIVE':
        return false; // Executive cannot assign roles
      default:
        return false;
    }
  };

  return {
    can: (permission: keyof Permission) => permissions[permission],
    hasRole: (role: Role) => userRole === role,
    canAssignRole,
    userRole,
    permissions
  };
};
